import { Controller } from "@hotwired/stimulus"

// Brand's chrome over a native <video>.
//
// Upstream keeps play/mute/volume/captions/fullscreen in a React context and
// re-renders the controls from it. Here the <video> element IS the state:
// every control writes to it, and its own events (play, pause, timeupdate,
// volumechange, loadedmetadata) sync the controls back. That keeps keyboard
// shortcuts, autoplay policies and the browser's picture-in-picture all in
// agreement without a second source of truth.
export default class extends Controller {
  static targets = [
    "container", "video", "title", "overlay", "overlayIcon",
    "playPause", "playIcon", "pauseIcon", "playPauseTooltip",
    "seekInput", "seekProgress", "currentTime", "totalTime",
    "captions", "captionText", "ccButton",
    "muteButton", "muteIcon", "unmuteIcon", "volumeControl", "volumeInput", "volumeProgress",
    "fullScreenButton", "screenFullIcon", "screenNormalIcon"
  ]

  connect() {
    this.captionsEnabled = false
    this.volumeBeforeMute = null
    // Captions are drawn by us, not the browser, so the track must be loaded
    // but not shown.
    const track = this.videoTarget.textTracks[0]
    if (track) track.mode = "hidden"
    this.syncPlaying()
    this.syncVolume()
    this.syncDuration()
  }

  // --- playback -----------------------------------------------------------

  togglePlaying() {
    if (this.videoTarget.paused) {
      this.videoTarget.play()
    } else {
      this.videoTarget.pause()
    }
  }

  syncPlaying() {
    const playing = !this.videoTarget.paused && !this.videoTarget.ended
    if (this.hasTitleTarget) this.titleTarget.classList.toggle("VideoPlayer__title--hidden", playing)
    this.overlayTarget.classList.toggle("VideoPlayer__playButtonOverlay--transparent", playing)
    this.overlayTarget.setAttribute("aria-label", playing ? "Pause" : "Play")
    this.overlayIconTarget.hidden = playing
    if (this.hasPlayPauseTarget) {
      this.playIconTarget.hidden = playing
      this.pauseIconTarget.hidden = !playing
      const label = playing ? "Pause video" : "Play video"
      this.playPauseTarget.setAttribute("aria-label", label)
      this.playPauseTooltipTarget.textContent = label
    }
  }

  rewind() {
    this.videoTarget.currentTime = 0
  }

  // --- seeking ------------------------------------------------------------

  seek(event) {
    this.videoTarget.currentTime = event.target.valueAsNumber
  }

  syncTime() {
    const time = this.videoTarget.currentTime
    if (this.hasSeekInputTarget) {
      this.seekInputTarget.value = time
      this.seekProgressTarget.value = time
      this.currentTimeTarget.textContent = formatTime(time, this.videoTarget.duration)
    }
    this.syncCaption(time)
  }

  syncDuration() {
    const duration = this.videoTarget.duration
    if (!this.hasSeekInputTarget || !Number.isFinite(duration)) return
    this.seekInputTarget.max = duration
    this.seekProgressTarget.max = duration
    this.totalTimeTarget.textContent = formatTime(duration)
  }

  // --- captions -----------------------------------------------------------

  toggleCaptions() {
    this.captionsEnabled = !this.captionsEnabled
    this.ccButtonTarget.classList.toggle("VideoPlayer__ccOff", !this.captionsEnabled)
    this.ccButtonTarget.setAttribute("aria-label", this.captionsEnabled ? "Disable captions" : "Enable captions")
    this.captionsTarget.hidden = !this.captionsEnabled
    this.syncCaption(this.videoTarget.currentTime)
  }

  syncCaption(time) {
    if (!this.captionsEnabled) return
    const cues = this.videoTarget.textTracks[0]?.cues
    if (!cues) return
    let text = ""
    for (const cue of cues) {
      if (time >= cue.startTime && time < cue.endTime) {
        text = cue.text
        break
      }
    }
    this.captionTextTarget.textContent = text
  }

  // --- volume -------------------------------------------------------------

  toggleMute() {
    if (this.videoTarget.volume === 0) {
      this.videoTarget.volume = this.volumeBeforeMute ?? 1
      this.volumeBeforeMute = null
    } else {
      this.volumeBeforeMute = this.videoTarget.volume
      this.videoTarget.volume = 0
    }
  }

  setVolume(event) {
    this.videoTarget.volume = Math.max(0, Math.min(1, event.target.valueAsNumber))
  }

  syncVolume() {
    const volume = this.videoTarget.volume
    const muted = volume === 0
    if (this.hasMuteButtonTarget) {
      this.muteIconTarget.hidden = !muted
      this.unmuteIconTarget.hidden = muted
      this.muteButtonTarget.setAttribute("aria-label", muted ? "Unmute" : "Mute")
    }
    if (this.hasVolumeInputTarget) {
      this.volumeInputTarget.value = volume
      this.volumeProgressTarget.value = volume
    }
  }

  // --- fullscreen ---------------------------------------------------------

  toggleFullScreen() {
    if (document.fullscreenElement === this.containerTarget) {
      document.exitFullscreen()
    } else {
      this.containerTarget.requestFullscreen()
    }
  }

  syncFullScreen() {
    const full = document.fullscreenElement === this.containerTarget
    this.containerTarget.classList.toggle("VideoPlayer__container--fullscreen", full)
    if (!this.hasFullScreenButtonTarget) return
    this.screenFullIconTarget.hidden = full
    this.screenNormalIconTarget.hidden = !full
    this.fullScreenButtonTarget.setAttribute("aria-label", full ? "Exit full screen" : "Full screen")
  }

  // --- keyboard -----------------------------------------------------------

  // The same shortcuts upstream binds: space/k play-pause, m mute, f
  // fullscreen, c captions, arrows seek. Only when focus is on the player
  // itself, so a range input keeps its own arrow keys.
  keydown(event) {
    if (event.target !== this.containerTarget && event.target !== this.videoTarget &&
        event.target !== this.overlayTarget) return
    switch (event.key) {
      case " ":
      case "k":
        event.preventDefault()
        this.togglePlaying()
        break
      case "m":
        this.toggleMute()
        break
      case "f":
        this.toggleFullScreen()
        break
      case "c":
        if (this.hasCcButtonTarget) this.toggleCaptions()
        break
      case "ArrowLeft":
        this.videoTarget.currentTime -= 5
        break
      case "ArrowRight":
        this.videoTarget.currentTime += 5
        break
    }
  }
}

function pad(number) {
  return String(number).padStart(2, "0")
}

function formatTime(time, duration) {
  if (!Number.isFinite(time) || time < 0) return "00:00"
  if (duration && time > duration) time = duration
  const minutes = pad(Math.floor((time % 3600) / 60))
  const seconds = pad(Math.floor(time % 60))
  if (time > 3600) return `${pad(Math.floor(time / 3600))}:${minutes}:${seconds}`
  return `${minutes}:${seconds}`
}
